import type { NavPermissions } from "./types";

type NavPathFlag = {
	[Key in keyof NavPermissions]: NavPermissions[Key] extends boolean ? Key : never;
}[keyof NavPermissions];

interface NavPathRule {
	readonly flag: NavPathFlag;
	readonly prefix: string;
	readonly requires?: NavPathFlag;
}

/** Longest prefix first so `/settings/workspace/subscriptions` is not treated as workspace settings. */
const NAV_PATH_RULES: readonly NavPathRule[] = [
	{ prefix: "/timer", flag: "canSeeTimer" },
	{ prefix: "/settings/workspace/subscriptions", flag: "canSeeSubscriptions" },
	{ prefix: "/settings/workspace", flag: "canSeeWorkspaceAdminSection" },
	{ prefix: "/settings/projects", flag: "canSeeProjects" },
	{ prefix: "/settings/clients", flag: "canSeeClients" },
	{ prefix: "/settings/tags", flag: "canSeeTags" },
	{ prefix: "/settings/logs", flag: "canSeeLogs" },
	{ prefix: "/testing", flag: "canSeeTesting" },
	{ prefix: "/settings/team", flag: "canSeeWorkspaceAdminSection" },
	{ prefix: "/team-activity", flag: "canSeeTeamActivity" },
	{ prefix: "/profitability", flag: "canSeeProfitability" },
	{ prefix: "/supervise", flag: "canSeeSupervise" },
	{ prefix: "/dashboard", flag: "canSeeDashboard" },
	{ prefix: "/automation", flag: "canSeeAutomation" },
	{ prefix: "/calendar", flag: "canSeeFeed", requires: "canUseFeedEvents" },
	{ prefix: "/reports", flag: "canSeeDashboard" },
	{ prefix: "/feed", flag: "canSeeFeed", requires: "canUseFeedEvents" },
];

const stripTrailingSlash = (pathname: string): string =>
	pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

const matchesPrefix = (pathname: string, prefix: string): boolean =>
	pathname === prefix || pathname.startsWith(`${prefix}/`);

/** Paths with no rule stay open (e.g. `/account/profile`) so a deny redirect cannot loop. */
export const canAccessNavPath = (pathname: string, permissions: NavPermissions): boolean => {
	const path = stripTrailingSlash(pathname);
	const rule = NAV_PATH_RULES.find((item) => matchesPrefix(path, item.prefix));
	if (!rule) {
		return true;
	}
	return permissions[rule.flag] && (rule.requires === undefined || permissions[rule.requires]);
};

const FALLBACK_PATHS: readonly {
	readonly flag: NavPathFlag;
	readonly path: (permissions: NavPermissions) => string;
	readonly requires?: NavPathFlag;
}[] = [
	{ flag: "canSeeTimer", path: () => "/timer" },
	{ flag: "canSeeDashboard", path: (permissions) => permissions.dashboardUrl ?? "/dashboard" },
	{ flag: "canSeeFeed", path: () => "/feed", requires: "canUseFeedEvents" },
	{ flag: "canSeeTeamActivity", path: () => "/team-activity" },
	{ flag: "canSeeSupervise", path: (permissions) => permissions.superviseDefaultPath },
	{ flag: "canSeeProjects", path: () => "/settings/projects" },
	{ flag: "canSeeWorkspaceAdminSection", path: () => "/settings/workspace" },
];

/** First nav area this user may open. Used when `/timer` is denied. */
export const firstAllowedNavPath = (permissions: NavPermissions): string => {
	for (const item of FALLBACK_PATHS) {
		if (permissions[item.flag] && (item.requires === undefined || permissions[item.requires])) {
			return item.path(permissions);
		}
	}
	return "/account/profile";
};
